import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function CharteDontologiePage() {
  const handleDownload = () => {
    const content = document.getElementById('legal-content');
    if (!content) return;

    // Créer le contenu HTML pour l'impression
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Charte de Déontologie - Clinique Sainte Famille</title>
          <style>
            @page { 
              margin: 2cm; 
              size: A4;
            }
            body { 
              font-family: Arial, sans-serif; 
              padding: 0; 
              margin: 0;
              line-height: 1.6; 
              color: #333; 
              font-size: 12px;
            }
            h1 { 
              color: #1e40af; 
              font-size: 24px; 
              margin-bottom: 20px; 
              page-break-after: avoid;
            }
            h2 { 
              color: #1e40af; 
              font-size: 18px; 
              margin-top: 25px; 
              margin-bottom: 12px; 
              page-break-after: avoid;
            }
            h3 { 
              color: #1e40af; 
              font-size: 14px; 
              margin-top: 15px; 
              margin-bottom: 8px; 
              page-break-after: avoid;
            }
            p, li { 
              margin-bottom: 8px; 
              page-break-inside: avoid;
            }
            ul { 
              margin-left: 20px; 
            }
            .info-box { 
              background: #f8f9fa; 
              padding: 12px; 
              border-left: 4px solid #1e40af;
              margin: 12px 0; 
              page-break-inside: avoid;
            }
            .grid { 
              display: block; 
            }
            .grid > div { 
              margin-bottom: 10px; 
              page-break-inside: avoid;
            }
            section { 
              page-break-inside: avoid; 
              margin-bottom: 20px;
            }
            @media print {
              body { print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `;

    // Créer un blob avec le contenu
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Ouvrir dans une nouvelle fenêtre pour l'impression
    const printWindow = window.open(url, '_blank', 'width=800,height=600');
    
    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
    } else {
      // Fallback : téléchargement direct du fichier
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Charte-Deontologie-Clinique-Sainte-Famille.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line text-lg"></i>
              Télécharger en PDF
            </button>
          </div>
          <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/10 p-8 md:p-12" id="legal-content">
            <div className="mb-8">
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-stethoscope-line text-3xl text-clinic-blue"></i>
              </div>
              <h1 className="text-4xl font-bold text-clinic-blue-dark mb-2">Charte de Déontologie Médicale</h1>
              <p className="text-gray-600">Engagement des professionnels de santé</p>
            </div>

            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">Préambule</h2>
                <p className="text-gray-700 leading-relaxed">
                  La présente charte définit les principes déontologiques et les engagements que tout professionnel de santé 
                  exerçant au sein de la Clinique Sainte Famille ou utilisant sa plateforme numérique s'engage à respecter. 
                  Ces principes sont conformes au <strong>Code de déontologie médicale du Bénin</strong> et à la 
                  <strong> loi n°2017-20 sur la protection des données personnelles</strong>.
                </p>
                <div className="bg-clinic-blue/5 border-l-4 border-clinic-blue p-4 rounded-lg mt-4">
                  <p className="text-sm text-clinic-blue-dark">
                    <i className="ri-information-line mr-2"></i>
                    L'exercice de la médecine est une profession réglementée soumise au respect du secret médical 
                    et des règles déontologiques en vigueur au Bénin.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">1. Secret Médical et Confidentialité</h2>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-clinic-green pl-4">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2">1.1 Obligation de Secret</h3>
                    <p className="text-gray-700 mb-3">
                      Le médecin est tenu au secret professionnel pour tout ce qui lui a été confié ou qu'il a découvert 
                      dans l'exercice de sa profession. Ce secret couvre l'ensemble des informations concernant le patient.
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Informations médicales (diagnostics, traitements, résultats d'examens)</li>
                      <li>Données personnelles du patient</li>
                      <li>Circonstances de la consultation</li>
                      <li>Toute information confiée par le patient</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-clinic-green pl-4">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2">1.2 Protection des Données Numériques</h3>
                    <p className="text-gray-700 mb-3">
                      Le médecin s'engage à protéger les données médicales numériques avec la même rigueur que les dossiers papier :
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Utiliser uniquement des connexions sécurisées</li>
                      <li>Ne jamais partager ses identifiants de connexion</li>
                      <li>Se déconnecter systématiquement après chaque session</li>
                      <li>Verrouiller son poste de travail en cas d'absence</li>
                      <li>Ne pas accéder aux dossiers sans raison médicale légitime</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <i className="ri-alert-line mr-2"></i>
                      <strong>Important :</strong> Tous les accès aux dossiers médicaux sont tracés et journalisés. 
                      Tout accès non justifié constitue une violation du secret médical.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">2. Devoirs Envers les Patients</h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-clinic-blue/5 p-4 rounded-2xl">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2 flex items-center">
                      <i className="ri-heart-pulse-line mr-2 text-clinic-blue"></i>
                      Qualité des Soins
                    </h3>
                    <p className="text-sm text-gray-700">Dispenser des soins consciencieux, attentifs et conformes aux données acquises de la science</p>
                  </div>

                  <div className="bg-clinic-blue/5 p-4 rounded-2xl">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2 flex items-center">
                      <i className="ri-user-heart-line mr-2 text-clinic-blue"></i>
                      Respect de la Dignité
                    </h3>
                    <p className="text-sm text-gray-700">Respecter la dignité, l'autonomie et les convictions de chaque patient</p>
                  </div>

                  <div className="bg-clinic-blue/5 p-4 rounded-2xl">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2 flex items-center">
                      <i className="ri-information-line mr-2 text-clinic-blue"></i>
                      Information du Patient
                    </h3>
                    <p className="text-sm text-gray-700">Informer le patient de manière claire et loyale sur son état de santé et les traitements proposés</p>
                  </div>

                  <div className="bg-clinic-blue/5 p-4 rounded-2xl">
                    <h3 className="font-semibold text-clinic-blue-dark mb-2 flex items-center">
                      <i className="ri-hand-heart-line mr-2 text-clinic-blue"></i>
                      Consentement Éclairé
                    </h3>
                    <p className="text-sm text-gray-700">Obtenir le consentement libre et éclairé du patient avant tout acte médical</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">3. Utilisation de la Plateforme Numérique</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-clinic-blue-dark mb-2">3.1 Accès aux Dossiers Médicaux</h3>
                    <p className="text-gray-700 mb-3">Le médecin s'engage à :</p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>N'accéder qu'aux dossiers des patients dont il assure le suivi</li>
                      <li>Consulter les dossiers uniquement dans le cadre de la prise en charge médicale</li>
                      <li>Ne jamais accéder à son propre dossier ou à celui de ses proches</li>
                      <li>Signaler immédiatement toute tentative d'accès non autorisé</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-clinic-blue-dark mb-2">3.2 Saisie et Mise à Jour des Données</h3>
                    <p className="text-gray-700 mb-3">Le médecin doit :</p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Renseigner les dossiers de manière complète, exacte et lisible</li>
                      <li>Mettre à jour les informations médicales après chaque consultation</li>
                      <li>Documenter tous les actes médicaux réalisés</li>
                      <li>Saisir les prescriptions de manière claire et précise</li>
                      <li>Archiver les résultats d'examens et documents médicaux</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-clinic-blue-dark mb-2">3.3 Gestion des Rendez-vous</h3>
                    <p className="text-gray-700 mb-3">Le médecin s'engage à :</p>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Tenir à jour ses disponibilités sur la plateforme</li>
                      <li>Respecter les horaires de consultation</li>
                      <li>Prévenir en cas d'empêchement ou de modification d'agenda</li>
                      <li>Assurer la continuité des soins en cas d'absence</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">4. Devoirs Envers les Confrères</h2>
                <p className="text-gray-700 mb-4">
                  Les médecins se doivent assistance et respect mutuel. Ils doivent entretenir entre eux des rapports 
                  de bonne confraternité.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Respecter les décisions et prescriptions des confrères</li>
                  <li>Collaborer dans l'intérêt du patient</li>
                  <li>Partager les informations médicales nécessaires à la continuité des soins</li>
                  <li>Ne pas dénigrer un confrère auprès d'un patient</li>
                  <li>Signaler à la direction toute pratique contraire à la déontologie</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">5. Indépendance Professionnelle</h2>
                <div className="bg-clinic-green/10 border-2 border-clinic-green/30 p-6 rounded-2xl">
                  <p className="text-gray-700 mb-3">
                    Le médecin doit exercer sa profession en toute indépendance et liberté de jugement. 
                    Il ne peut aliéner son indépendance professionnelle sous quelque forme que ce soit.
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>Le médecin s'engage à :</strong>
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Prendre ses décisions médicales uniquement dans l'intérêt du patient</li>
                    <li>Refuser toute pression commerciale ou administrative contraire à l'éthique</li>
                    <li>Ne pas prescrire de médicaments ou examens inutiles</li>
                    <li>Déclarer tout conflit d'intérêt potentiel</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">6. Formation Continue</h2>
                <p className="text-gray-700 mb-4">
                  Le médecin a le devoir d'entretenir et de perfectionner ses connaissances médicales. 
                  Il doit prendre toutes dispositions nécessaires pour participer à des actions de formation continue.
                </p>
                <div className="bg-gray-50 p-6 rounded-2xl">
                  <p className="text-gray-700 mb-2">
                    <strong>Engagement de formation :</strong>
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Participer aux formations organisées par la clinique</li>
                    <li>Se tenir informé des évolutions scientifiques et techniques</li>
                    <li>Partager ses connaissances avec les confrères</li>
                    <li>Adapter ses pratiques aux recommandations médicales actualisées</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">7. Gestion des Situations d'Urgence</h2>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    <strong>Obligation de porter secours :</strong> Le médecin doit toujours porter secours à une personne 
                    en danger immédiat, sauf s'il s'expose lui-même à un danger ou s'il existe d'autres personnes plus aptes.
                  </p>
                  <p className="text-gray-700">
                    <strong>Continuité des soins :</strong> Le médecin doit assurer la continuité des soins. 
                    En cas d'absence, il doit organiser le relais avec un confrère.
                  </p>
                  <div className="bg-clinic-blue/5 border-l-4 border-clinic-blue p-4 rounded-lg">
                    <p className="text-sm text-clinic-blue-dark">
                      <i className="ri-information-line mr-2"></i>
                      En cas d'urgence vitale, le médecin peut accéder au dossier médical d'un patient 
                      même s'il n'en assure pas habituellement le suivi.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">8. Interdictions et Limites</h2>
                <div className="bg-red-50 border-2 border-red-200 p-6 rounded-2xl">
                  <h3 className="font-semibold text-red-800 mb-3">Il est strictement interdit au médecin de :</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-2">
                    <li>Exercer la médecine sous l'influence de substances altérant le jugement</li>
                    <li>Établir des certificats de complaisance</li>
                    <li>Prescrire des traitements sans examen du patient</li>
                    <li>Divulguer le secret médical (sauf exceptions légales)</li>
                    <li>Accepter des avantages en nature ou en espèces de la part des patients</li>
                    <li>Faire de la publicité personnelle</li>
                    <li>Dénigrer un confrère ou un établissement concurrent</li>
                    <li>Exercer une activité commerciale liée à la santé créant un conflit d'intérêt</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">9. Sanctions Disciplinaires</h2>
                <p className="text-gray-700 mb-4">
                  Le non-respect de la présente charte peut entraîner des sanctions disciplinaires internes, 
                  sans préjudice des poursuites ordinales ou pénales prévues par la loi.
                </p>
                <div className="bg-gray-50 p-6 rounded-2xl space-y-2">
                  <p className="text-gray-700"><strong>Sanctions possibles :</strong></p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Avertissement</li>
                    <li>Blâme</li>
                    <li>Suspension temporaire d'accès à la plateforme</li>
                    <li>Suspension temporaire d'exercice au sein de la clinique</li>
                    <li>Révocation et signalement à l'Ordre des Médecins</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">10. Engagement et Signature</h2>
                <div className="bg-clinic-blue/5 border-2 border-clinic-blue/20 p-6 rounded-2xl">
                  <p className="text-gray-700 mb-4">
                    En acceptant cette charte, le médecin s'engage solennellement à :
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    <li>Respecter l'ensemble des principes déontologiques énoncés</li>
                    <li>Exercer la médecine avec conscience et probité</li>
                    <li>Protéger le secret médical et les données des patients</li>
                    <li>Utiliser la plateforme numérique de manière responsable et sécurisée</li>
                    <li>Maintenir ses compétences à jour par la formation continue</li>
                    <li>Collaborer loyalement avec ses confrères et l'administration</li>
                  </ul>
                  <div className="bg-white p-4 rounded-lg border-2 border-clinic-blue/30">
                    <p className="text-sm text-gray-600 italic">
                      "Je jure de respecter les principes de cette charte et d'exercer la médecine 
                      dans le respect de la vie humaine, de la dignité des patients et des règles 
                      déontologiques de ma profession."
                    </p>
                  </div>
                </div>
              </section>

              <section className="bg-clinic-blue/5 p-6 rounded-2xl border-2 border-clinic-blue/20">
                <h2 className="text-2xl font-bold text-clinic-blue-dark mb-4">Contact et Signalement</h2>
                <p className="text-gray-700 mb-4">
                  Pour toute question concernant cette charte ou pour signaler un manquement déontologique :
                </p>
                <div className="space-y-2 text-gray-700">
                  <p><i className="ri-mail-line mr-2 text-clinic-blue"></i><strong>Email :</strong> clinicfam@yahoo.com</p>
                  <p><i className="ri-phone-line mr-2 text-clinic-blue"></i><strong>Téléphone :</strong> +229 01 95 40 58 91</p>
                  <p><i className="ri-map-pin-line mr-2 text-clinic-blue"></i><strong>Adresse :</strong> 99QX+7W, Cotonou Bénin - 04 BP 127 lot 1862 Fifadji</p>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mt-4">
                  <p className="text-sm text-yellow-800">
                    <i className="ri-alert-line mr-2"></i>
                    Les signalements peuvent être effectués de manière confidentielle et seront traités 
                    avec la plus grande discrétion.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
